<template>
  <div class="journey-layout">
    <JourneyHero :ride="ride" :days="days" :stops="stops" @explore="scrollToChapters"/>
    <div ref="chapters">
      <JourneyChapters
        v-if="summaryAvailable"
        :stops="stops"
        :editable="canEdit"
        @open-stop="scrollToRideDay($event.dayIndex)"
        @edit="$refs.stopsEditor.open()"
      />
    </div>
    <JourneyStopsEditor v-if="canEdit" ref="stopsEditor" :ride="ride" :days="days"/>
    <JourneyStory :ride="ride"/>
    <RideCopyOffer :ride="ride"/>
    <b-container class="pt-4">
      <div class="single_ride_sumary">
        <main>
          <section>
            <RideMedia :ride="ride"/>
            <div class="trip-date-section">
              <div class="container">
                <ClassicTimeline v-if="summaryAvailable"/>
                <PosterGeneralInfo :ride="ride"/>
              </div>
            </div>
          </section>
        </main>
      </div>
    </b-container>
  </div>
</template>

<script>
import RideCopyOffer from "@/components/RideSummary/RideCopyOffer";
import RideMedia from "@/components/RideSummary/RideMedia";
import ClassicTimeline from "@/designs/classic/ClassicTimeline";
import PosterGeneralInfo from "@/designs/poster/PosterGeneralInfo";
import { scrollToRideDay } from "@/utils/scrollToRideElement";
import { mapGetters } from "vuex";
import { buildPosterStops, POSTER_SETTINGS_VERSION } from "@/utils/posterStops";
import { designSettings } from "@/utils/rideDesigns";
import JourneyHero from "./JourneyHero";
import JourneyChapters from "./JourneyChapters";
import JourneyStory from "./JourneyStory";
import JourneyStopsEditor from "./JourneyStopsEditor";
import "./journeyPage.css";

/**
 * The journey trip page, a modern magazine look: a full-screen cover photo
 * with the title, the dates and the trip's numbers (JourneyHero), the stops
 * as a swipeable gallery of tall photo cards (JourneyChapters), the story
 * with the ride manager's card beside it (JourneyStory), then the classic
 * summary and map and the poster's general information. The stops are the
 * poster's automatic ones (buildPosterStops), so both designs cut a trip the
 * same way; this design has no settings of its own. The page header is
 * compact (compactHeader in src/designs/index.js), as the cover carries the
 * title.
 *
 * The rest of the page -- the summary, the trip plan's days and cards, the
 * maps and the day bar -- is restyled in journeyPage.css, under the page's
 * ride-design-journey class, so the shared components stay as they are.
 *
 * A design layout -- see src/designs/index.js for the contract every layout
 * keeps (props, and the .single_ride_sumary element).
 */
export default {
  name: 'JourneyLayout',
  components: {
    RideCopyOffer,
    RideMedia,
    ClassicTimeline,
    PosterGeneralInfo,
    JourneyHero,
    JourneyChapters,
    JourneyStory,
    JourneyStopsEditor,
  },
  props: {
    ride: { type: Object, required: true },
    days: { type: Array, default: () => [] },
    summaryAvailable: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters(['compareToUserId', 'isUserManager', 'isRideLocked']),
    // The poster's automatic stops, or the manager's own (JourneyStopsEditor).
    stops() {
      return buildPosterStops(this.days, {
        settings: designSettings(this.ride, 'journey', POSTER_SETTINGS_VERSION),
      });
    },
    // The same people who may edit the poster (PosterLayout.canEditPoster).
    canEdit() {
      return this.isUserManager && this.compareToUserId(this.ride.authorId) && !this.isRideLocked;
    },
  },
  methods: {
    scrollToRideDay,
    scrollToChapters() {
      const el = this.$refs.chapters;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  },
};
</script>
